import { omitBy } from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import { format } from 'date-fns';
import { lawfirm, titleOpinionApi } from '~/api';
import { RefreshIndicator } from '~/components';
import { useAsync, useAuth, useForm } from '~/hooks';
import Layout from '../templates/DefaultLayout';
import { OrderHistoryContext } from './context';
import FilterModal from './FilterModal';
//import { applyFilters } from './filters';
import OrdersGrid from './OrdersGrid';
import AttachmentsModal from './AttachmentsModal';

const getOffset = (page, pageSize = 10) => {
    return {
        offset: page * pageSize,
        size: pageSize
    };
};

const formatFilters = filters => {
    const ignored = ['firm', 'status', 'statusOptions', 'submissionStart', 'submissionEnd', 'filenumber'];
    const validFilters = omitBy(filters, (value, key) => ignored.includes(key) || !value || (typeof value === 'string' && value.length === 0));

    let status = undefined;
    if (filters.status?.length > 0) {
        status = filters.status.reduce((result, current, i) => {
            result += `${i > 0 ? ',' : ''}${current.value}`;
            return result;
        }, '');
    }

    let firmIds = undefined;
    if (filters.firm?.length > 0) {
        firmIds = filters.firm.reduce((result, current, i) => {
            result += `${i > 0 ? ',' : ''}${current.value}`;
            return result;
        }, '');
    }

    const formattedFilters = {
        ...validFilters,
        fileNumber: filters.filenumber.length > 0 ? filters.filenumber : undefined,
        start: filters.submissionStart ? format(filters.submissionStart, 'yyyy-MM-dd') : undefined,
        end: filters.submissionEnd ? format(filters.submissionEnd, 'yyyy-MM-dd') : undefined,
        status, 
        firmIds
    };
    return omitBy(formattedFilters, value => !value);
};

const TryonOrderHistory = () => {
    const { isTryon } = useAuth();

    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    let statusOptions = [
        { label: 'PTO Started', value: 1 },
        { label: 'PTO Submitted', value: 2 },
        { label: 'FTO Started', value: 3 },
        { label: 'FTO Submitted', value: 4 },
        { label: 'STF Started', value: 7 },
        { label: 'STF Submitted', value: 8 }
    ];

    const [filtersOpen, setFiltersOpen] = useState(false);

    const firmData = useAsync(() => {
        if (isTryon)
            return lawfirm.getFirms();
        return;
    }, false);

    const {
        state: { values: filters },
        fieldActions,
        formActions
    } = useForm({
        initialState: {
            filenumber: '',
            orderNumber: '',
            ownerName: '',
            submissionStart: null,
            submissionEnd: null,
            address: '',
            status: null,
            statusOptions,
            firm: null
        }
    });

    const gridData = useAsync(async params => {
        const formattedFilters = params?.ignoreFilters ? {} : formatFilters(filters);
        const nextPage = (params?.page || params?.page === 0) ? params.page : page;
        const offset = getOffset(nextPage, pageSize);
        
        const data = await titleOpinionApi.list({ ...offset, filters: formattedFilters })
            .then(result => {
                let orders = [...result.data];
                
                return { total: result.total, orders };
            });

        setTotal(data.total);
        return flattenData(data.orders);
    }, false);
    gridData.total = total;
    
    

    let [filteredRows, setFilteredRows] = useState([]);
    
    const handlePageChange = nextPage => {
        setPage(nextPage);
        fetchAll({ page: nextPage });
    };

    const handlePageSizeChange = newPageSize => {
        setPageSize(newPageSize);
    };

    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const showAttachmentsModal = () => setAttachmentsOpen(true);
    const hideAttachmentsModal = () => setAttachmentsOpen(false);
    const [attachments, setAttachments] = useState([]);

    /*
    const clean = filters => {
        return omitBy(filters, (value, key) => value == null || /options/i.test(key));
    };
    */

    const filterData = () => {
        //const filtered = applyFilters(gridData.value, clean(filters));
        //setFilteredRows(filtered);
        fetchAll();
        setFiltersOpen(false);
    };

    const filterOutstanding = async () => {
        await fieldActions.onChange([
            { label: 'PTO Submitted', value: 2 },
            { label: 'FTO Started', value: 3 }
        ], 'status');

        filterData();
    };

    const clearFilters = () => {
        formActions.reset();
        //setFilteredRows(gridData.value);
        fetchAll({ ignoreFilters: true });
        setFiltersOpen(false);
    };

    const fetchAll = async (params) => {
        await gridData.execute(params);

        if (isTryon)
            await firmData.execute();
    };

    useEffect(fetchAll, [pageSize]);

    useEffect(() => setFilteredRows(gridData.value), [gridData.value, gridData.status]);

    const ctxValue = {
        gridData,
        firmData,
        apply: filterData,
        clear: clearFilters,
        filterOutstanding,
        filters,
        fieldActions,
        filteredRows,
        attachments,
        setAttachments,
        showAttachmentsModal,
        hideAttachmentsModal
    };

    return (
        <Layout title='Order History'>
            <div className='container py-4'>
                <OrderHistoryContext.Provider value={ctxValue}>
                    <RefreshIndicator fetch={fetchAll} />
                    <div className='mt-6'>
                        <button
                            className='py-2 px-4 flex text-blue-dark text-sm font-semibold bg-gray-50 focus:outline-none items-center justify-center rounded-full'
                            onClick={() => setFiltersOpen(true)}
                        >
                            <MdFilterList size='1.125em' className='mr-1' />
                            Filter
                        </button>
                    </div>
                    <OrdersGrid pageSize={pageSize} currentPage={page} handlePageChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} />
                    <FilterModal isOpen={filtersOpen} setOpen={setFiltersOpen} />
                    <AttachmentsModal isOpen={attachmentsOpen} />
                </OrderHistoryContext.Provider>
            </div>
        </Layout>
    );
};

export default TryonOrderHistory;

function flattenData(data = []) {
    if (data.length < 1)
        return data;

    return data.map(d => {
        return {
            ...d,
            owners: flattenOwners(d)
        };
    }).sort((a, b) => {
        const aMod = DateTime.fromISO(a.modified);
        const bMod = DateTime.fromISO(b.modified);

        if (aMod > bMod || !bMod.isValid)
            return -1;
        if (aMod < bMod || !aMod.isValid)
            return 1;
        return 0;
    });
}

function flattenOwners(data) {
    if (Array.isArray(data.owners)) {
        return data.owners.map((o => {
            let owner = {};
            if (o.entity) {
                owner.entityName = o.entity.name;
            }
            if (o.primary) {
                owner.primary = { ...o.primary };
            }
            if (o.secondary) {
                owner.secondary = { ...o.secondary };
            }

            return owner;
        }));
    }

    return [];
}
